import { useState, useEffect } from "react";
import { useLanguage } from "@/lib/LanguageContext";
import api, { formatPrice } from "@/lib/api";

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const { t, l, lang } = useLanguage();

  const getCat = (item) => lang === "kz" ? (item.category_kz || item.category) : (item.category_ru || item.category);

  useEffect(() => {
    api.get("/menu").then((r) => {
      setItems(r.data);
      const cats = [...new Set(r.data.map((i) => lang === "kz" ? (i.category_kz || i.category) : (i.category_ru || i.category)))];
      setCategories(cats);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [lang]);

  const filtered = activeCategory === "all" ? items : items.filter((i) => getCat(i) === activeCategory);

  return (
    <div className="py-24 md:py-32" data-testid="menu-page">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-[0.3em] text-primary font-sans mb-2">{t("nav.menu")}</p>
          <h1 className="text-5xl md:text-6xl font-serif tracking-tight">{t("menuPage.title")}</h1>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto pb-2" data-testid="menu-categories">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-4 py-2 text-sm font-sans whitespace-nowrap transition-all ${
              activeCategory === "all" ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}
            data-testid="menu-filter-all"
          >
            {t("common.all")}
          </button>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCategory(c)}
              className={`px-4 py-2 text-sm font-sans whitespace-nowrap transition-all ${
                activeCategory === c ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`menu-filter-${c}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-card animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="no-menu-items">
            <p className="font-serif text-2xl mb-2">{t("menuPage.empty")}</p>
          </div>
        ) : (
          <div className="space-y-1" data-testid="menu-list">
            {activeCategory === "all"
              ? categories.map((cat) => {
                  const catItems = items.filter((i) => getCat(i) === cat);
                  if (catItems.length === 0) return null;
                  return (
                    <div key={cat} className="mb-8">
                      <h2 className="font-serif text-2xl mb-4 text-primary">{cat}</h2>
                      <div className="space-y-1">
                        {catItems.map((item) => (
                          <MenuRow key={item.id} item={item} l={l} />
                        ))}
                      </div>
                    </div>
                  );
                })
              : filtered.map((item) => <MenuRow key={item.id} item={item} l={l} />)
            }
          </div>
        )}
      </div>
    </div>
  );
}

function MenuRow({ item, l }) {
  return (
    <div
      className="flex items-center justify-between py-3 px-4 hover:bg-card/50 transition-colors border-b border-border"
      data-testid={`menu-item-${item.id}`}
    >
      <div className="flex-1 min-w-0">
        <span className="font-sans text-sm">{l(item)}</span>
        {l(item, "description") && <span className="text-xs text-muted-foreground ml-2">{l(item, "description")}</span>}
      </div>
      <span className="text-primary font-semibold font-sans text-sm ml-4 whitespace-nowrap">{formatPrice(item.price)}</span>
    </div>
  );
}
